import {
  Component, OnInit, Input, OnChanges, Output, EventEmitter,
  SimpleChanges, TemplateRef, ChangeDetectorRef, OnDestroy
} from '@angular/core';
import * as moment from 'moment-timezone';
import { ActivatedRoute, Router } from '@angular/router';
import { KeyValue } from '@angular/common';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { cloneDeep, orderBy, get, isObject, debounce } from 'lodash';
import { Subject } from 'rxjs';
import { ActionResponse } from '../../utils/action-utils/action-response.interface';
import { OrderChangeOutput } from '../../utils/list-grid/order-change-output.interface';
import { FieldValueChangeOutput } from '../../utils/list-grid/field-value-change-output.interface';
import { crudTableDefaultItemActionsLength, CrudTableViewConfig, DropdownMenuItem, MenuActionBehavior } from '../../utils';
import { CrudViewColumnType } from '../../utils/list-grid/crud-view-column-type.enum';
import { ActionBehavior } from '../../utils/action-utils/action-behavior.enum';
import { ActionView } from '../../utils/action-utils/action-view.enum';
import { HorizontalPosition } from '../../utils/action-utils/horizontal-position.enum';
import { CrudTableViewColumn } from '../../utils/list-grid/crud-table-view-column.interface';
import { Action } from '../../utils/action-utils/action.interface';
import { RappiderPaginationService } from '../../services/pagination/pagination.service';
import { CRUD_TABLE_DEFAULT_PAGINATION } from '../../utils/list-grid';
import { TemplatingService } from '../../services/templating/templating.service';
import { ColumnFilter } from './components/grid-filter/utils/column-filter.interface';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { TagListDirectionMode } from '../../utils/tag-list/tag-list-direction-mode.enum';
import { FilterValue } from './components/grid-filter/utils/filter-value.interface';
import { TextStyleType } from '../../utils/edit-form/crud-view-text-style-type.enum';
import { FieldFormattingColorUtil } from '../../utils/field-formatting/field-formatting-color.util';
import { ShowDateFormatService } from 'libs/shared/src/lib/services/list-grid-service/show-date-format.service'

interface VisibleColumnsOutput {
  visibleColumns: string[];
  allColumns: string[];
  hiddenColumns: string[];
}

@Component({
  selector: 'rappider-list-grid',
  templateUrl: './list-grid.component.html',
  styleUrls: ['./list-grid.component.scss']
})
export class RappiderListGridComponent implements OnInit, OnChanges, OnDestroy {

  @Input() config: CrudTableViewConfig;
  @Input() data: any[];
  @Input() loading: boolean;
  @Input() loadingMessage = 'Fetching data, please wait....';
  /* col span border */
  @Input() borderless: boolean;
  @Input() showEllipsisTextWithPopover = false; // for CrudViewColumnType.Text
  @Input() showEllipsisLinkWithPopover = false; // for CrudViewColumnType.Link
  @Input() customDropdownTemplate: TemplateRef<any>;

  /* redirection url data to be replaced in specified url template */
  @Input() redirectUrlData: Record<string, any>;
  @Input() pageNumber = 1;
  @Input() searchPlaceholder = 'Type to search';
  // Add a search button next to the search bar
  @Input() searchButtonActive = false;
  // It disables the search while typing from the keyboard. It's for situations where the user wants to search by clicking the button
  @Input() searchActive = true;
  /* Adds a pointer style for the grid rows so that users can understand the row is clickable */
  @Input() enableRowPointer = false;
  // When a row in the grid is clicked, it highlights the entire row with a color to indicate it is selected
  @Input() selectAndMark = false;

  @Output() listActionClick = new EventEmitter<ActionResponse>();
  @Output() rowActionClick = new EventEmitter<ActionResponse>();
  @Output() columnActionClick = new EventEmitter<ActionResponse>();
  @Output() orderChange = new EventEmitter<OrderChangeOutput>();
  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() fieldValueChange = new EventEmitter<FieldValueChangeOutput>();
  @Output() columnItemClick = new EventEmitter<any>();
  @Output() listActionDropdownItemClick = new EventEmitter<any>();
  @Output() paginationChange = new EventEmitter<NzTableQueryParams>();
  @Output() searchTextChange = new EventEmitter<string>();
  // It emits the search text when the search button is clicked
  @Output() searchTextChangeOnButtonClick = new EventEmitter<string>();
  @Output() linkCellClicked = new EventEmitter<{ column: CrudTableViewColumn; data: any }>();
  // It creates an overflow list component to display a nested folder structure
  @Output() overflowlistItemClick = new EventEmitter<string>();
  @Output() visibleColumnsChange = new EventEmitter<VisibleColumnsOutput>();
  @Output() listGridVisibilityColumnsChange = new EventEmitter<string[]>();

  CrudViewColumnType = CrudViewColumnType;
  ActionBehavior = ActionBehavior;
  ActionView = ActionView;
  TagListDirectionMode = TagListDirectionMode;

  sort: KeyValue<string, 'asc' | 'desc' | null>;
  currentSortState: { key: string; value: 'ascend' | 'descend' | null } | null = null;
  isAllSelected = false;
  isIndeterminate = false;
  selectedComponents: { [key: string]: boolean } = {};
  displayedData = [];
  visibleColumns = [];
  visibleData;
  searchText = null;
  // When a row is clicked, it stores the information of that row
  selectedRow: any;

  previousVisibilityColumns: string[] = [];

  /* AI Search */
  searchAIText: string | undefined;

  /* TODO: this path will be moved to a service */
  noImageUrl = 'assets/images/no-image.png';

  HorizontalPosition = HorizontalPosition;

  columnFilters!: ColumnFilter[];
  appliedFilters: { [key: string]: FilterValue } = {};
  hasActiveFilters = false;

  leftListActions: Action[] = [];
  rightListActions: Action[] = [];

  /* defining exec template fn */
  execTemplate = this.templatingService.execTemplate;

  visibilityColumnsChangeSubject = new Subject<void>();
  debouncedVisibilityColumnsEmit: (() => void) & { cancel(): void };


  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private paginationService: RappiderPaginationService,
    private templatingService: TemplatingService,
    private cdr: ChangeDetectorRef,
    private showDateFormatService: ShowDateFormatService
  ) {
    this.debouncedVisibilityColumnsEmit = debounce(() => {
      this.emitListGridVisibilityColumnsChange();
    });
  }

  ngOnInit() {
    /* Show only visible fields */
    this.filterColumnsByVisibility();
    this.displayedData = cloneDeep(this.data || []);
    this.initFilterVariable();
    this.checkActiveFilters();
    this.emitVisibleColumnsChange();
    this.setLeftListActions();
    this.setRightListActions();
    if (this.config?.order) {
      this.sort = {
        key: this.config.order.fieldName,
        value: this.config.order.type
      };
      this.currentSortState = {
        key: this.config.order.fieldName,
        value: this.config.order.type === 'asc' ? 'ascend' : 'descend'
      };
      this.filterData(this.searchText);
      setTimeout(() => this.updateSortIcons(), 0);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data) {
      this.orderData();
      const data = cloneDeep(this.data || []);
      this.displayedData = [...data];
      this.initDefaults();
      this.config = { ...this.config };

      this.setLeftListActions();
      this.setRightListActions();

      this.filterData(this.searchText);
      this.checkActiveFilters();
      this.refreshStatus();
    }
    if (changes.config) {
      this.filterColumnsByVisibility();
      this.initFilterVariable();
      if (this.sort || this.config?.order) {
        this.filterData(this.searchText);
        setTimeout(() => this.updateSortIcons(), 0);
      }
      this.checkActiveFilters();
      this.emitVisibleColumnsChange();
    }

    if (changes.config && changes.config.currentValue?.visibilityColumns !== changes.config.previousValue?.visibilityColumns) {
      this.filterColumnsByVisibility();
      this.initFilterVariable();
      this.emitVisibleColumnsChange();
    }

    if (changes.loading) {
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    if (this.debouncedVisibilityColumnsEmit) {
      this.debouncedVisibilityColumnsEmit.cancel();
    }
    this.visibilityColumnsChangeSubject.complete();
  }

  forceEmitVisibilityColumnsChange() {
    if (this.hasVisibilityColumnsChanged()) {
      this.emitListGridVisibilityColumnsChange();
      this.previousVisibilityColumns = [...this.visibleColumns];
    }
  }

  emitListGridVisibilityColumnsChange() {
    this.listGridVisibilityColumnsChange.emit([...this.visibleColumns]);
  }

  hasVisibilityColumnsChanged(): boolean {
    if (this.visibleColumns.length !== this.previousVisibilityColumns.length) {
      return true;
    }

    return !this.visibleColumns.every((column, index) =>
      column === this.previousVisibilityColumns[index]
    );
  }

  initDefaults() {
    this.setDefaultPagination();
    this.setDefaultItemActionLength();
  }

  setDefaultItemActionLength() {
    if (this.config) {
      if (this.config?.itemActionsLength == null) {
        this.config.itemActionsLength = crudTableDefaultItemActionsLength;
      }
    }
  }

  setDefaultPagination() {
    if (this.config) {
      if (!this.config?.showPagination) {
        this.config.pageSize = this.displayedData.length;
      } else if (!this.config.pageSize) {
        this.config.pageSize = CRUD_TABLE_DEFAULT_PAGINATION.PAGE_SIZE;
      }
    }
  }


  orderData() {
    if (this.data?.length && this.config?.order) {
      const orderConfig = this.config.order;
      this.data = orderBy(this.data, orderConfig.fieldName, orderConfig.type);
    }
  }

  emitFieldValueChange(column: CrudTableViewColumn, value: any, data: any) {
    const updatedData: any = {};
    updatedData[column.fieldName] = value;
    const emitBody: FieldValueChangeOutput = {
      columnData: data,
      updatedData: updatedData
    };
    this.fieldValueChange.emit(emitBody);
  }

  refreshStatus(): void {
    const validData = this.displayedData.filter(c => c);
    const isAllSelected = validData.length && validData?.filter(v => v.disabled !== true).every(item => item['selected'] === true);
    const isAllUnselected = validData?.filter(v => v.disabled !== true).every(item => item['selected'] === false);
    this.isAllSelected = isAllSelected;
    this.isIndeterminate = !isAllSelected && !isAllUnselected;
    /* emit selected items */
    let selectedItems: any;

    if (this.isMultipleSelectionEnabled()) {
      selectedItems = this.displayedData.filter(item => item.selected);
    } else {
      selectedItems = this.displayedData.find(item => item.selected) || null;
    }

    this.selectionChange.emit(selectedItems);
  }

  onBulkSelectionChange(isSelected: boolean): void {
    if (this.isMultipleSelectionEnabled()) {
      this.displayedData = this.displayedData.map(item => {
        if (!item.disabled) {
          return {
            ...item,
            selected: isSelected
          }
        } else {
          return { ...item }
        }
      });
    }
    this.refreshStatus();
  }

  selectItems(isSelected?: boolean, rowIndex?: number) {
    if (this.isMultipleSelectionEnabled()) {
      const selectedData = this.displayedData.find((item, index) => index === rowIndex);
      selectedData.selected = !selectedData.selected;
    } else {
      const alreadySelectedData = this.displayedData.find((item, index) => index !== rowIndex && item.selected);
      if (alreadySelectedData) {
        alreadySelectedData.selected = false;
      }
      const selectedData = this.displayedData[rowIndex];
      selectedData.selected = isSelected;
    }
    this.refreshStatus();
  }

  isMultipleSelectionEnabled() {
    if (this.displayedData?.every(data => data?.disabled === true)) {
      return false;
    }
    return this.config?.enableMultipleSelection === true;
  }

  formatDate(dateValue) {
    if (!dateValue) {
      return null;
    }
    // Strict numerical format DD/MM/YYYY HH:mm:ss
    return moment.utc(dateValue).format('DD/MM/YYYY HH:mm:ss');
  }

  filterData(searchText: string): void {
    this.searchText = searchText;

    let filteredData = [...(this.data || [])];

    // Apply search filter
    if (searchText?.trim() && (this.config?.multipleSearchFields?.length || this.config?.defaultSearchField)) {
      // Prepare multiple search terms and normalize to lowercase
      const searchTerms = searchText.trim().toLowerCase().split(' ');

      // Filter data by matching search terms across multiple fields or a default field
      filteredData = this.data?.filter(item => {
        if (this.config?.multipleSearchFields?.length > 0) {
          return searchTerms.every(term =>
            this.config.multipleSearchFields.some(field => {
              const value = item[field]?.toString().toLowerCase();
              return value?.includes(term);
            })
          );
        }
        if (this.config?.defaultSearchField) {
          return searchTerms.every(term => {
            const value = item[this.config.defaultSearchField]?.toString().toLowerCase();
            return value?.includes(term);
          });
        }
        return true;
      }) || [];
    }

    Object.keys(this.appliedFilters).forEach(fieldName => {
      const filterValue = this.appliedFilters[fieldName];
      if (filterValue && filterValue.filterValue !== null && filterValue.filterValue !== undefined && filterValue.filterValue !== '') {
        filteredData = filteredData.filter(item => {
          const itemValue = this.getFieldValueByFieldName(item, fieldName);
          if (filterValue.condition === 'contains') {
            return itemValue?.toString().toLowerCase().includes(filterValue.filterValue?.toString().toLowerCase());
          }
          if (filterValue.condition === 'equals') {
            return itemValue === filterValue.filterValue;
          }
          if (filterValue.condition === 'startsWith') {
            return itemValue?.toString().toLowerCase().startsWith(filterValue.filterValue?.toString().toLowerCase());
          }
          if (filterValue.condition === 'endsWith') {
            return itemValue?.toString().toLowerCase().endsWith(filterValue.filterValue?.toString().toLowerCase());
          }
          return true;
        });
      }
    });

    // Apply sorting if exists
    if (this.sort && this.sort.key) {
      filteredData = orderBy(filteredData, this.sort.key, this.sort.value);
    }

    this.displayedData = filteredData;
    this.searchTextChange.emit(searchText);
    this.checkActiveFilters();
  }

  updateSearchText(searchText) {
    this.searchText = searchText;
  }

  searchTextEmit(searchText) {
    if (searchText === null) {
      this.searchText = null;
    }
    this.searchTextChangeOnButtonClick.emit(searchText);
  }

  clearSearchText() {
    this.searchText = null;
    this.filterData(null);
    this.checkActiveFilters();
  }

  convertToDate(dateValue) {
    if (!dateValue) {
      return null;
    }

    // Strict numerical format DD/MM/YYYY HH:mm
    return moment.utc(dateValue).format('DD/MM/YYYY HH:mm');
  }

  convertToDateTime(dateValue) {
    if (!dateValue) {
      return null;
    }

    // Strict numerical format DD/MM/YYYY
    return moment.utc(dateValue).format('DD/MM/YYYY');
  }

  onPaginationChange(paginationData: NzTableQueryParams) {
    this.paginationChange.emit(paginationData);
  }

  sortData(sort: KeyValue<string, string>) {
    this.currentSortState = {
      key: sort.key,
      value: sort.value as 'ascend' | 'descend' | null
    };

    if (sort.value === 'ascend') {
      this.sort = { key: sort.key, value: 'asc' };
    } else if (sort.value === 'descend') {
      this.sort = { key: sort.key, value: 'desc' };
    } else {
      this.sort = null;
      this.currentSortState = null;
    }

    if (this.sort?.key) {
      this.displayedData = orderBy([...this.displayedData], this.sort.key, this.sort.value);
    }

    this.updateSortIcons();
    this.cdr.detectChanges();
  }

  updateSortIcons() {
    document.querySelectorAll('.ant-table-column-sorter-up, .ant-table-column-sorter-down')
      .forEach(icon => icon.classList.remove('active'));

    if (this.currentSortState?.key) {
      const activeColumn = document.querySelector(`[nz-column-key="${this.currentSortState.key}"]`);
      if (activeColumn) {
        const isAscending = this.currentSortState.value === 'ascend';
        const targetIcon = activeColumn.querySelector(isAscending ? '.ant-table-column-sorter-up' : '.ant-table-column-sorter-down');
        targetIcon?.classList.add('active');
      }
    }
  }

  getVisibleColumns(columns) {
    return columns?.filter(column => this.visibleColumns.includes(column.fieldName));
  }

  initFilterVariable() {
    this.columnFilters = this.getVisibleColumns(this.config.columns)?.map(column => ({ fieldName: column.fieldName, filterValue: null, columnType: column.type }));
  }

  getVisibilityChangableColumnList(
    columns: CrudTableViewColumn[]
  ): CrudTableViewColumn[] {
    return columns?.filter(column => column.fieldName);
  }

  onFilterChange() {
    this.initFilterVariable();
    this.emitVisibleColumnsChange();
    if (this.config) {
      this.config = {
        ...this.config,
        visibilityColumns: [...this.visibleColumns]
      };
    }
  }

  onDataFilterChange() {
    if (this.visibleData?.length) {
      this.displayedData = this.data?.filter(d => this.visibleData?.includes(d.categoryId));
    } else {
      this.displayedData = this.data;
    }
  }

  onListActionClick(action: Action) {
    if (action.behavior === ActionBehavior.Route) {
      /* navigate */
      this.redirectToUrlByAction(
        action,
        this.redirectUrlData
      );
    } else if (action.behavior === ActionBehavior.Emit) {
      /* emit */
      const actionResponse: ActionResponse = {
        action: action
      };
      this.listActionClick.emit(actionResponse);
    }
  }
  cancel() {
  }
  onColumnActionClick(action: Action, data: any, rowIndex: number) {
    if (!action.disabled) {
      if (action.behavior === ActionBehavior.Route) {
        /* navigate */
        this.redirectToUrlByAction(action, data);
      } else if (action.behavior === ActionBehavior.Emit || action.behavior === ActionBehavior.CrudEditLink) {
        /* emit */
        const actionResponse: ActionResponse = {
          action: action,
          data: data,
          rowIndex: this.paginationService.getSkippedSizeByPagination(this.pageNumber) + rowIndex
        };
        this.columnActionClick.emit(actionResponse);
      }
    }
  }

  onRowActionClick(data: any) {
    this.selectedRow = data === this.selectedRow ? null : data;
    this.rowActionClick.emit(data);
  }

  redirectToUrlByAction(action: Action, data: any) {
    /* navigate */
    let url = action.redirectUrl;
    /* set id if specified */
    if (action.redirectUrl && data?.id) {
      url = this.execTemplate(action.redirectUrl, data);
    }

    /* check if inside grid-builder, emit event instead of navigation */
    if (this.isInsideGridBuilder()) {
      const actionResponse: ActionResponse = {
        action: action,
        data: data
      };
      this.columnActionClick.emit(actionResponse);
      return;
    }

    if (action?.redirectUrlMode === 'navigate') {
      this.router.navigate([url], { relativeTo: this.route?.parent });
    } else {
      this.router.navigateByUrl(url);
    }
  }

  /* check if current context is inside grid-builder component */
  isInsideGridBuilder(): boolean {
    return window.location.href.includes('grid-builder') ||
      document.querySelector('rappider-grid-builder') != null;
  }

  getAsArray(data: any): any[] {
    let result: any[] = [];
    if (Array.isArray(data)) {
      result = data;
    } else if (typeof data === 'object' && data !== null) {
      result = [data];
    } else if (typeof data === 'string' && data.trim() !== '') {
      result = this.stringToArray(data);
    }
    return result;
  }

  stringToArray(arrayAsString: string): string[] {
    if (!arrayAsString || arrayAsString.trim() === '') {
      return [];
    }
    const str = arrayAsString.trim();
    if (!str.startsWith('[') || !str.endsWith(']')) {
      return [];
    }
    try {
      const jsonStr = arrayAsString.replace(/'/g, '"');
      return JSON.parse(jsonStr);
    } catch (e) {
      console.error('Invalid input string:', str, e);
      return [];
    }
  }

  onRowOrderChange(event: CdkDragDrop<any[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      /* change order in the array */
      const eventPreviousIndex = this.paginationService.getSkippedSizeByPagination(this.pageNumber) + event.previousIndex;
      const eventCurrentIndex = this.paginationService.getSkippedSizeByPagination(this.pageNumber) + event.currentIndex;
      moveItemInArray(this.displayedData, eventPreviousIndex, eventCurrentIndex);
      this.displayedData = [...this.displayedData];
      /* emit order change */
      const orderStartIndex = this.paginationService.
        getSkippedSizeByPagination(this.pageNumber) + Math.min(event.previousIndex, event.currentIndex);
      const orderEndIndex = this.paginationService.
        getSkippedSizeByPagination(this.pageNumber) + Math.max(event.previousIndex, event.currentIndex);
      const orderChangeEmitData = {
        orderStartIndex: orderStartIndex,
        orderEndIndex: orderEndIndex,
        data: this.displayedData.slice(orderStartIndex, orderEndIndex + 1)
      };
      this.orderChange.emit(orderChangeEmitData);
    }
  }

  filterColumnsByVisibility() {
    if (this.config?.visibilityColumns && this.config.visibilityColumns.length > 0) {
      this.visibleColumns = this.config.visibilityColumns.filter(columnFieldName =>
        this.config.columns?.some(column => column.fieldName === columnFieldName)
      );
    } else {
      this.visibleColumns = (<CrudTableViewColumn[]>this.config.columns)?.filter(c => c.visible !== false).map(c => c.fieldName) || [];
    }
    this.previousVisibilityColumns = [...this.visibleColumns];
  }

  hasDropdownListAction(horizontalPosition: HorizontalPosition) {
    if (horizontalPosition === HorizontalPosition.Left) {
      return this.config.listActions.some(item => item.displayAsMenu && (item.horizontalPosition !== HorizontalPosition.Right));
    } else {
      return this.config.listActions.some(item => item.displayAsMenu && (item.horizontalPosition === HorizontalPosition.Right));
    }
  }

  onEmitItemClick(data: any) {
    this.columnItemClick.emit(data);
  }

  /* this function returns config function if exists else returns false */
  checkListOrderable(data: any[], item: any) {
    if (this.config?.handleOrderableFn) {
      return this.config.handleOrderableFn(data, item);
    } else {
      return false;
    }
  }

  /* this function returns config function if exists else returns true */
  checkActionButtonVisibility(data: any[], item: any, action: Action) {
    if (this.config?.handleVisibilityFn) {
      return this.config.handleVisibilityFn(data, item, action);
    } else {
      return true;
    }
  }

  /* this function returns config function if exists else returns false */
  checkActionButtonDisabled(data: any[], item: any, action: Action) {
    if (this.config?.handleDisableFn) {
      return this.config.handleDisableFn(data, item, action);
    } else {
      return false;
    }
  }

  /* this function returns config function if exists else returns false */
  checkActionButtonLoading(data: any[], item: any, action: Action) {
    if (this.config?.handleLoadingFn) {
      return this.config.handleLoadingFn(data, item, action);
    } else {
      return false;
    }
  }

  /* this function returns config function if exists else returns '' */
  checkActionButtonTooltip(data: any[], item: any, action: Action) {
    if (this.config?.handleTooltipFn) {
      return this.config.handleTooltipFn(data, item, action);
    } else {
      return '';
    }
  }

  isListGridDataObject(data: any): boolean {
    return isObject(data);
  }

  /* this functions checks visible buttons in config for dropdown menu visibility, if there is no visible button returns false */
  checkItemActionDropdownMenuVisiblity(displayedData: any[], item: any, itemActions: Action[]) {
    return itemActions.some(itemAction => this.checkActionButtonVisibility(displayedData, item, itemAction));
  }

  /**
   * Get count of visible actions for a specific item
   * @param displayedData - All displayed data
   * @param item - Current item
   * @param itemActions - All item actions
   * @returns Number of visible actions
   */
  getVisibleActionCount(displayedData: any[], item: any, itemActions: Action[]): number {
    return itemActions.filter(action => this.checkActionButtonVisibility(displayedData, item, action)).length;
  }

  /**
   * getting field value by nested field name
   * for ex.
   * {
   *   parent: {
   *     child: 5
   *   }
   * }
   *
   * fieldName: 'parent.child'
   *
   * lodash.get supports 'parent.child' field-name to get a nested field value
   *
   * @param {*} data
   * @param {string} fieldName
   * @return {*}
   * @memberof RappiderListGridComponent
   */
  getFieldValueByFieldName(data: any, fieldName: string) {
    return get(data, fieldName);
  }

  getAutoDateTimeFormat(dateString: string, jsonSchema) {
    if (!dateString) {
      return null;
    }
    return this.showDateFormatService.formatDate(dateString, jsonSchema?.format);
  }

  getDateTimeFormat(dateString: string): string | null {
    if (!dateString) {
      return null;
    }
    const hasTimeInfo = /T\d{2}:\d{2}/.test(dateString);
    return hasTimeInfo ? 'short' : 'shortDate';
  }

  onDataFilter(data) {
    this.displayedData = data;
    if (this.sort && this.sort.key) {
      this.displayedData = orderBy(this.displayedData, this.sort.key, this.sort.value);
    }
    this.checkActiveFilters();
  }

  checkActiveFilters() {
    const hasColumnFilters = Object.keys(this.appliedFilters).length > 0;
    this.hasActiveFilters = hasColumnFilters;
  }

  onFilterValueChange(fieldName: string, filterValue: FilterValue) {
    const filterIndex = this.columnFilters.findIndex(f => f.fieldName === fieldName);
    if (filterIndex !== -1) {
      this.columnFilters[filterIndex].filterValue = filterValue;

      // Only update applied filters if the filter value is not null/empty
      // This ensures only saved filters appear in the filter section
      if (filterValue && filterValue.filterValue !== null && filterValue.filterValue !== undefined && filterValue.filterValue !== '') {
        this.appliedFilters[fieldName] = filterValue;
      } else {
        delete this.appliedFilters[fieldName];
      }

      this.checkActiveFilters();
      this.applyAllFilters();
    }
  }

  onRemoveFilter(fieldName: string) {
    const filterIndex = this.columnFilters.findIndex(f => f.fieldName === fieldName);
    if (filterIndex !== -1) {
      this.columnFilters[filterIndex].filterValue = null;
      delete this.appliedFilters[fieldName];
      this.checkActiveFilters();
      // Re-apply all remaining filters
      this.applyAllFilters();
    }
  }

  onRemoveSearchFilter() {
    this.searchText = null;
    this.filterData(null);
    this.checkActiveFilters();
  }

  clearAllFilters() {
    this.searchText = null;
    this.columnFilters.forEach(filter => {
      filter.filterValue = null;
    });
    this.appliedFilters = {};
    this.checkActiveFilters();
    this.filterData(null);
  }

  applyAllFilters() {
    let filteredData = [...(this.data || [])];

    if (this.searchText?.trim() && (this.config?.multipleSearchFields?.length || this.config?.defaultSearchField)) {
      const searchTerms = this.searchText.trim().toLowerCase().split(' ');
      filteredData = this.data?.filter(item => {
        if (this.config?.multipleSearchFields?.length > 0) {
          return searchTerms.every(term =>
            this.config.multipleSearchFields.some(field => {
              const value = item[field]?.toString().toLowerCase();
              return value?.includes(term);
            })
          );
        }
        if (this.config?.defaultSearchField) {
          return searchTerms.every(term => {
            const value = item[this.config.defaultSearchField]?.toString().toLowerCase();
            return value?.includes(term);
          });
        }
        return true;
      }) || [];
    }

    Object.keys(this.appliedFilters).forEach(fieldName => {
      const filterValue = this.appliedFilters[fieldName];
      if (filterValue && filterValue.filterValue !== null && filterValue.filterValue !== undefined && filterValue.filterValue !== '') {
        filteredData = filteredData.filter(item => {
          const itemValue = this.getFieldValueByFieldName(item, fieldName);
          if (filterValue.condition === 'contains') {
            return itemValue?.toString().toLowerCase().includes(filterValue.filterValue?.toString().toLowerCase());
          }
          if (filterValue.condition === 'equals') {
            return itemValue === filterValue.filterValue;
          }
          if (filterValue.condition === 'startsWith') {
            return itemValue?.toString().toLowerCase().startsWith(filterValue.filterValue?.toString().toLowerCase());
          }
          if (filterValue.condition === 'endsWith') {
            return itemValue?.toString().toLowerCase().endsWith(filterValue.filterValue?.toString().toLowerCase());
          }
          return true;
        });
      }
    });

    if (this.sort && this.sort.key) {
      filteredData = orderBy(filteredData, this.sort.key, this.sort.value);
    }

    this.displayedData = filteredData;
  }

  onListActionDropdownItemClick(action: DropdownMenuItem) {
    if (action.actionBehavior === MenuActionBehavior.Emit) {
      this.listActionDropdownItemClick.emit(action);
    }
  }

  onLinkCellClicked(column: CrudTableViewColumn, data: any) {
    this.linkCellClicked.emit({
      column: column,
      data: data
    });
  }

  onOverflowlistItemClick(item) {
    this.overflowlistItemClick.emit(item);
  }

  emitVisibleColumnsChange() {
    this.visibleColumnsChange.emit({
      visibleColumns: this.visibleColumns,
      allColumns: this.config.columns?.map(c => c.fieldName) || [],
      hiddenColumns: this.config.columns?.filter(c => c.visible === false).map(c => c.fieldName) || []
    });
  }

  /**
   * Set left positioned list actions
   */
  setLeftListActions() {
    if (this.config?.listActions?.length) {
      this.leftListActions = this.config?.listActions?.filter(action =>
        !action.horizontalPosition || action.horizontalPosition === HorizontalPosition.Left
      ) || [];
    } else {
      this.leftListActions = [];
    }
  }

  /**
   * Set right positioned list actions
   */
  setRightListActions() {
    if (this.config?.listActions?.length) {
      this.rightListActions = this.config?.listActions?.filter(action =>
        action.horizontalPosition === HorizontalPosition.Right
      ) || [];
    } else {
      this.rightListActions = [];
    }
  }

  onAISearchKeyPress(event: KeyboardEvent) {

    if (event.key === 'Enter') {
      console.log('AI Search triggered:', this.searchAIText);
    }
  }

  onSearchButtonActiveKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.searchTextChangeOnButtonClick.emit(this.searchText);
      this.filterData(this.searchText);
    }
  }

  /**
   * Gets CSS class for table row based on config function
   * @param data - Row data
   * @returns CSS class string
   */
  getRowClass(data: any): string {
    const classes = [];

    if (this.selectedRow === data && this.selectAndMark) {
      classes.push('selectedRow');
    }

    if (this.config?.handleRowClassFn) {
      const customClass = this.config.handleRowClassFn(data);
      if (customClass) {
        classes.push(customClass);
      }
    }

    return classes.join(' ');
  }

  /**
   * Get text color value for column header
   */
  getColumnTextColorValue(column: CrudTableViewColumn): string {
    return FieldFormattingColorUtil.getTextColorValue(column?.textColor);
  }

  /**
   * Get background color value for column header
   */
  getColumnBackgroundColorValue(column: CrudTableViewColumn): string {
    return FieldFormattingColorUtil.getTextColorValue(column?.backgroundColor);
  }

  /**
   * Get style properties for column header
   */
  getColumnStyleProperties(column: CrudTableViewColumn): { fontWeight: string; fontStyle: string; textDecoration: string } {
    const style = column?.textStyle;
    if (!style) {
      return { fontWeight: 'normal', fontStyle: 'normal', textDecoration: 'none' };
    }

    let fontWeight = 'normal';
    let fontStyle = 'normal';
    let textDecoration = 'none';

    if (style === TextStyleType.Bold) {
      fontWeight = 'bold';
    } else if (style === TextStyleType.Italic) {
      fontStyle = 'italic';
    } else if (style === TextStyleType.BoldItalic) {
      fontWeight = 'bold';
      fontStyle = 'italic';
    } else if (style === TextStyleType.Underline) {
      textDecoration = 'underline';
    }

    return { fontWeight, fontStyle, textDecoration };
  }

  /**
   * Get combined cell styles for column data cells (includes formatting + cssStyle)
   */
  getColumnCellStyles(column: CrudTableViewColumn): { [key: string]: any } {
    const styles: { [key: string]: any } = {};

    // Apply existing cssStyle if any
    if (column?.cssStyle) {
      Object.assign(styles, column.cssStyle);
    }

    // Apply text color
    const textColor = this.getColumnTextColorValue(column);
    if (textColor) {
      styles.color = textColor;
    }

    // Apply background color
    const backgroundColor = this.getColumnBackgroundColorValue(column);
    if (backgroundColor) {
      styles['background-color'] = backgroundColor;
    }

    // Apply text style
    const styleProps = this.getColumnStyleProperties(column);
    if (styleProps.fontWeight !== 'normal') {
      styles['font-weight'] = styleProps.fontWeight;
    }
    if (styleProps.fontStyle !== 'normal') {
      styles['font-style'] = styleProps.fontStyle;
    }
    if (styleProps.textDecoration !== 'none') {
      styles['text-decoration'] = styleProps.textDecoration;
    }

    return styles;
  }

  /**
   * Get header cell styles (background color for entire header cell)
   */
  getColumnHeaderCellStyles(column: CrudTableViewColumn): { [key: string]: any } {
    const styles: { [key: string]: any } = {};

    // Apply existing cssStyle if any
    if (column?.cssStyle) {
      Object.assign(styles, column.cssStyle);
    }

    // Apply background color to entire header cell
    const backgroundColor = this.getColumnBackgroundColorValue(column);
    if (backgroundColor) {
      styles['background-color'] = backgroundColor;
    }

    return styles;
  }

  /**
   * Get header text styles (text color and text style for header text only)
   */
  getColumnHeaderTextStyles(column: CrudTableViewColumn): { [key: string]: any } {
    const styles: { [key: string]: any } = {};

    // Apply text color
    const textColor = this.getColumnTextColorValue(column);
    if (textColor) {
      styles.color = textColor;
    }

    // Apply text style
    const styleProps = this.getColumnStyleProperties(column);
    if (styleProps.fontWeight !== 'normal') {
      styles['font-weight'] = styleProps.fontWeight;
    }
    if (styleProps.fontStyle !== 'normal') {
      styles['font-style'] = styleProps.fontStyle;
    }
    if (styleProps.textDecoration !== 'none') {
      styles['text-decoration'] = styleProps.textDecoration;
    }

    return styles;
  }

  /**
   * Get link text styles (text color and text style for link elements)
   * This ensures link color overrides default link styles
   */
  getColumnLinkTextStyles(column: CrudTableViewColumn): { [key: string]: any } {
    const styles: { [key: string]: any } = {};

    // Apply text color - will be overridden by CSS with !important
    const textColor = this.getColumnTextColorValue(column);
    if (textColor) {
      styles.color = textColor;
      // Add data attribute for CSS targeting
      styles['data-column-color'] = textColor;
    }

    // Apply text style
    const styleProps = this.getColumnStyleProperties(column);
    if (styleProps.fontWeight !== 'normal') {
      styles['font-weight'] = styleProps.fontWeight;
    }
    if (styleProps.fontStyle !== 'normal') {
      styles['font-style'] = styleProps.fontStyle;
    }
    if (styleProps.textDecoration !== 'none') {
      styles['text-decoration'] = styleProps.textDecoration;
    }

    return styles;
  }

}
